import {booleanAttribute, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, input} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormArray, ReactiveFormsModule} from "@angular/forms";
import {NgClass} from "@angular/common";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {GruppeMedlemListeModell, MedlemId} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatButtonModule} from "@angular/material/button";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {TranslateModule} from "@ngx-translate/core";
import {DeltakerSkjemaType, MøteRedigerDeltakerKontroller} from "web-ng2/moduler/møte/komponenter/møte-rediger-deltaker/møte-rediger-deltaker";
import {toSignal} from "@angular/core/rxjs-interop";
import {oppdaterGuiVedEndringIFormArrayLengde, skjemaendringerSomObserverbar} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {map} from "rxjs/operators";
import {medlemmerAktive} from "felles-ng2/modell/medlemModellHjelpere";

const debug = initDebugger('MøteRedigerDeltakereKontroller');

export type DeltakereSkjemaType = ReturnType<typeof MøteRedigerDeltakereKontroller.opprettSkjema>;
export type DeltakereSkjemaVerdier = DeltakereSkjemaType['value'];
export type Deltaker = { medlem: { id: MedlemId }; skalSignere?: boolean; erStemmeberettiget?: boolean };

@Component({
    selector: 'moete-rediger-deltakere',
    templateUrl: './møte-rediger-deltakere.html',
    styleUrls: ['./møte-rediger-deltakere.scss'],
    imports: [
        NgClass,
        ReactiveFormsModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatTooltipModule,
        MatButtonModule,
        TipsKomponent,
        MatSlideToggleModule,
        TranslateModule,
        MøteRedigerDeltakerKontroller
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøteRedigerDeltakereKontroller {

    /** Oppretter nytt tomt skjema */
    static opprettSkjema() {
        return new FormArray<DeltakerSkjemaType>([])
    }

    /** Nullstiller angitt skjema og populerer det med angitte deltakere, valgt og skalSignere feltene gis verdier basert på angitte deltakere  */
    static repopulerSkjema(skjema: DeltakereSkjemaType, medlemmer: GruppeMedlemListeModell[], deltakere: Deltaker[], destroyRef: DestroyRef) {
        skjema.clear({emitEvent: false});
        medlemmerAktive(medlemmer)
          .map(medlem => {
              const deltakerFraMalmøte = deltakere.find(deltaker => deltaker.medlem.id == medlem.id)
              return MøteRedigerDeltakerKontroller.opprettSkjemarad(medlem, erIkkeNil(deltakerFraMalmøte), deltakerFraMalmøte?.skalSignere ?? false, deltakerFraMalmøte?.erStemmeberettiget ?? false, destroyRef)
          })
          .forEach(skjemarad => skjema.push(skjemarad, {emitEvent: false}));
        //først nå emitter vi en valueChanges event (foregående clear og push kall har ikke ført til eventer)
        skjema.updateValueAndValidity();
    }

    /** Returnerer liste med MedlemId for alle gruppemedlemmer i angitt `skjemaverdier` som har valgt=true.
     * Forventer at angitt skjemaverdier er hentet fra skjema opprettet med {@link MøteRedigerDeltakereKontroller#opprettSkjema} */
    static somGruppemedlemIdForValgteMedlemmer(skjemaverdier: DeltakereSkjemaVerdier): MedlemId[] {
        return skjemaverdier
          .filter(rad => rad.valgt)
          .map(rad => rad.medlem!.id);
    }

    /** Returnerer liste med MedlemId for alle rader i angitt `skjemaverdier` som har valgt=true og skalSignere=true.
     * Forventer at angitt skjemaverdier er hentet fra skjema opprettet med {@link MøteRedigerDeltakereKontroller#opprettSkjema} */
    static somGruppemedlemIdForMedlemmerSomSkalSignere(skjemaverdier: DeltakereSkjemaVerdier): MedlemId[] {
        return skjemaverdier
          .filter(rad => rad.valgt && rad.skalSignere)
          .map(rad => rad.medlem!.id);
    }

    skjema = input.required<DeltakereSkjemaType>();
    skalSignereValgSynlig = input(true, {transform: booleanAttribute});

    antallValgt = toSignal(skjemaendringerSomObserverbar(this.skjema).pipe(
      map(rader => rader.filter(rad => rad.valgt)),
      map(valgteRader => valgteRader.length)
    ), {initialValue: 0});
    alleValgt = computed(() => this.antallValgt() == this.skjema().length);
    ingenValgt = computed(() => this.antallValgt() == 0);

    constructor(cdRef: ChangeDetectorRef) {
        oppdaterGuiVedEndringIFormArrayLengde(this.skjema, cdRef);
    }

    endreAlle() {
        const erValgt = !this.alleValgt();
        this.skjema().patchValue(this.skjema().value.map(medlemSkjemaVerdi => ({valgt: erValgt})));
    }

    // private hjelpemetoder

}
